import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Logo } from '../components/Logo';
import { AdminSessionBanner } from '../components/AdminSessionBanner';
import { authedFetch } from '../lib/authed-fetch';
import { redirectToPortalLogin, useCurrentUser } from '../lib/use-current-user';
import '../admin.css';

/**
 * Reminder opt-out management, mounted at /admin/reminder-opt-outs.
 *
 * Two jobs: let an admin suppress reminder emails for someone who asked
 * off the list verbally, and show what the self-service unsubscribe link
 * at the bottom of reminder emails has been doing. The history table is
 * the part that matters over time — it's the only place that shows an
 * opt-out which was later reversed.
 *
 * Reminder emails only. Booking confirmations, reschedules, and
 * cancellations still send; SMS is untouched.
 */

interface OptOutRow {
  email: string;
  channel: string;
  optedOutAt: string;
  source: string;
  actor: string | null;
  note: string | null;
}

interface OptOutEventRow {
  email: string;
  action: 'opt_out' | 'opt_in';
  source: string;
  actor: string | null;
  note: string | null;
  createdAt: string;
}

interface ApiResponse {
  ok?: boolean;
  rows?: OptOutRow[];
  events?: OptOutEventRow[];
  error?: string;
  message?: string;
}

const TZ = 'America/New_York';

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

// Action-neutral on purpose. These labels fill a "How" column that
// describes opt-OUTS and opt-INS in the same table, so they name the
// route the change came in through rather than the change itself —
// "Added by admin" read wrong sitting next to a REMINDERS ON badge.
const SOURCE_LABEL: Record<string, string> = {
  email_link: 'Email link',
  one_click: 'Mail client',
  admin: 'Admin panel',
  import: 'Import',
};

function sourceLabel(source: string): string {
  return SOURCE_LABEL[source] ?? source;
}

export function AdminReminderOptOuts() {
  const auth = useCurrentUser();

  const [rows, setRows] = useState<OptOutRow[]>([]);
  const [events, setEvents] = useState<OptOutEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (auth.status === 'unauthenticated') {
      window.location.href = redirectToPortalLogin();
    }
  }, [auth.status]);

  const load = useCallback(async () => {
    try {
      const res = await authedFetch('/api/admin/reminder-opt-outs', { cache: 'no-store' });
      const json = (await res.json()) as ApiResponse;
      if (!res.ok || !json.ok) {
        setErr(json.message ?? json.error ?? `HTTP ${res.status}`);
        return;
      }
      setErr(null);
      setRows(json.rows ?? []);
      setEvents(json.events ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auth.status !== 'authenticated' || !auth.user.isAdmin) {
      setLoading(false);
      return;
    }
    void load();
  }, [auth, load]);

  async function mutate(action: 'add' | 'remove', targetEmail: string, targetNote?: string) {
    setBusy(true);
    setNotice(null);
    try {
      const res = await authedFetch('/api/admin/reminder-opt-outs', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ action, email: targetEmail, note: targetNote }),
      });
      const json = (await res.json()) as ApiResponse;
      if (!res.ok || !json.ok) {
        setErr(json.message ?? json.error ?? `HTTP ${res.status}`);
        return;
      }
      setErr(null);
      setNotice(
        action === 'add'
          ? `${targetEmail} will no longer receive reminder emails.`
          : `${targetEmail} will receive reminder emails again.`
      );
      if (action === 'add') {
        setEmail('');
        setNote('');
      }
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Network error');
    } finally {
      setBusy(false);
    }
  }

  function handleAdd(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    void mutate('add', trimmed, note.trim() || undefined);
  }

  return (
    <div className="admin-app">
      <AdminSessionBanner />
      <header className="admin-header">
        <div className="admin-header-inner">
          <Logo className="admin-logo" />
          <div>
            <h1 className="admin-title">Reminder Opt-Outs</h1>
            <p className="admin-sub">
              Prospects suppressed from meeting <strong>reminder emails</strong> · booking
              confirmations, reschedules and cancellations still send · text reminders
              unaffected
            </p>
          </div>
        </div>
        <nav className="admin-nav">
          <a href="/admin/routing" className="admin-nav-link">
            Routing Audit Log
          </a>
          <a href="/admin/advisors" className="admin-nav-link">
            Advisors &amp; Pools
          </a>
          <a href="/admin/availability" className="admin-nav-link">
            Advisor Availability
          </a>
          <a
            href="/admin/reminder-opt-outs"
            className="admin-nav-link admin-nav-link-active"
          >
            Reminder Opt-Outs
          </a>
        </nav>
      </header>

      <main className="admin-main">
        {err && (
          <div className="admin-err" role="alert">
            <strong>Error:</strong> {err}
          </div>
        )}
        {notice && (
          <div
            role="status"
            style={{
              background: '#EAF3EA',
              border: '1px solid #B7D3B7',
              borderRadius: 4,
              padding: '10px 14px',
              margin: '0 0 18px',
              fontSize: 14,
            }}
          >
            {notice}
          </div>
        )}

        <form
          onSubmit={handleAdd}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'flex-end',
            margin: '0 0 26px',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
            Email address
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="prospect@example.com"
              style={{ padding: '8px 10px', minWidth: 280, fontSize: 14 }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
            Reason (optional)
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Asked to stop reminders on the 7/29 call"
              style={{ padding: '8px 10px', minWidth: 320, fontSize: 14 }}
            />
          </label>
          <button type="submit" disabled={busy} style={{ padding: '9px 18px', fontSize: 14 }}>
            {busy ? 'Saving…' : 'Opt out'}
          </button>
        </form>

        {loading && rows.length === 0 && !err && <p className="admin-empty">Loading…</p>}
        {!loading && rows.length === 0 && !err && (
          <p className="admin-empty">Nobody is opted out of reminder emails right now.</p>
        )}

        {rows.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Opted out (ET)</th>
                  <th>How</th>
                  <th>By</th>
                  <th>Note</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.email} className="admin-row">
                    <td>{r.email}</td>
                    <td className="admin-cell-time">{formatTime(r.optedOutAt)}</td>
                    <td>{sourceLabel(r.source)}</td>
                    <td>{r.actor ?? '—'}</td>
                    <td className="admin-cell-detail">{r.note ?? '—'}</td>
                    <td>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void mutate('remove', r.email)}
                        style={{ padding: '5px 12px', fontSize: 13 }}
                      >
                        Resume reminders
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {events.length > 0 && (
          <>
            <h2 style={{ fontSize: 17, fontWeight: 'normal', margin: '34px 0 10px' }}>
              History
            </h2>
            <p className="admin-sub" style={{ margin: '0 0 12px' }}>
              Every opt-out and opt-in, including ones since reversed.
            </p>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>When (ET)</th>
                    <th>Email</th>
                    <th>Change</th>
                    <th>How</th>
                    <th>By</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev, i) => (
                    <tr key={`${ev.email}-${ev.createdAt}-${i}`} className="admin-row">
                      <td className="admin-cell-time">{formatTime(ev.createdAt)}</td>
                      <td>{ev.email}</td>
                      <td>
                        <span
                          className={
                            'admin-badge ' +
                            (ev.action === 'opt_out' ? 'admin-badge-fallback' : 'admin-badge-ok')
                          }
                        >
                          {ev.action === 'opt_out' ? 'opted out' : 'reminders on'}
                        </span>
                      </td>
                      <td>{sourceLabel(ev.source)}</td>
                      <td>{ev.actor ?? '—'}</td>
                      <td className="admin-cell-detail">{ev.note ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
